import * as React from 'react';

import {Button} from 'shells/button/button';
import {ButtonMover} from 'shells/button-mover/button-mover';
import {ConditionalCheckboxInput} from 'shells/form/conditional-checkbox-input';
import {FormField} from 'shells/form/form-field';
import {HelpButton} from 'shells/help/help-button';
import {CopyableLink} from 'shells/link/copyable-link';
import type {ListOptionWithIcon} from 'shells/select-option-components/option-with-icon';

import type {EditionUpdateInput, EditionFragment as Edition} from 'nutshell-graphql-types';

import {GraphQLSelectUsers} from '../../../core/select/graphql-select-users';
import {useGetSessionUser} from '../../../core/use-get-session-user';

import {ABTestComparisonFields} from '../a-b-testing/a-b-test-comparison-fields';
import {SENT_STATUSES} from '../edition-content-page/edition-content-page';
import {useCampaignSetOwner} from '../graphql/hooks/use-campaign-set-owner';

import {FromField} from './from-field';
import {ToField} from './to-field';
import {UtmFormModal} from './utm-modals/utm-form-modal';
import {UtmHelpModal} from './utm-modals/utm-help-modal';

import styles from './editions-details-form.module.css';

const PREVIEW_STATUSES = [
    'SENDING',
    'SENT',
    'HALTED',
    'IN_REVIEW',
    'COMPARISON_SENDING',
    'COMPARISON_WAITING',
];

type Props = {
    edition: Edition;
    isABTesting?: boolean;
    setIsPreparingToSend: (arg1: boolean) => void;
    onEditionSave: (saveData: EditionUpdateInput) => Promise<any>;
    onSetSelectedEditorTab: (arg1: string) => void;
    onIsSavingDetailsChange: (isSavingDetails: boolean) => void;
};

export function EditionsDetailsForm(props: Props) {
    const {edition} = props;

    const [showUtmHelpModal, setShowUtmHelpModal] = React.useState<boolean>(false);
    const [showUtmFormModal, setShowUtmFormModal] = React.useState<boolean>(false);

    const {user} = useGetSessionUser();
    const {setOwner, errorMessage} = useCampaignSetOwner();

    const showPreviewMode =
        PREVIEW_STATUSES.indexOf(edition.status) !== -1 ||
        (NutClientConfig.hasNewAudienceManagementNUT14721 && edition.status === 'SCHEDULED');

    const editionToDisplay =
        edition.comparison && edition.comparison.winnerEdition
            ? edition.comparison.winnerEdition
            : edition;

    const isBroadcast = edition.campaign.type === 'BROADCAST';
    const isTemplate = edition.campaign.type === 'TEMPLATE';
    const userFacingCampaignType = isBroadcast ? 'broadcast' : 'newsletter';

    const hasFullAccessToCampaigns =
        user && user.permissions ? user.permissions.canAccessFullCampaigns : false;

    // @ts-expect-error
    const isEditionSent = SENT_STATUSES.includes(edition.status);
    const isEditionScheduled = edition.status === 'SCHEDULED';

    // shouldShowNewAudienceManagement and sendToComponent capture three possible states:
    // Showing the new audience management field, the old one, or no field at all.
    const shouldShowNewAudienceManagement =
        NutClientConfig.hasNewAudienceManagementNUT14721 &&
        !isEditionSent &&
        !isEditionScheduled &&
        !isTemplate;

    const handleOwnerChange = React.useCallback(
        (selectedUser: ListOptionWithIcon) => {
            props.onIsSavingDetailsChange(true);
            setOwner({
                campaignId: edition.campaign.id,
                userId: selectedUser.value,
            }).then(() => {
                props.onIsSavingDetailsChange(false);
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [edition, setOwner]
    );

    const handleClear = React.useCallback(() => {
        props.onIsSavingDetailsChange(true);
        setOwner({
            campaignId: edition.campaign.id,
            userId: null,
        }).then(() => {
            props.onIsSavingDetailsChange(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [edition, setOwner]);

    return (
        <>
            <form className={styles.container}>
                <div>
                    {isBroadcast && hasFullAccessToCampaigns ? (
                        <FormField
                            innerId='owner'
                            label='Owner'
                            tooltipInfo={
                                <div>
                                    The user who owns this {userFacingCampaignType}. Users with
                                    limited campaign access can only use the{' '}
                                    {userFacingCampaignType}s they own. Users with full access to
                                    Nutshell campaigns are able to use every{' '}
                                    {userFacingCampaignType}.
                                </div>
                            }
                            errorMessage={errorMessage}
                        >
                            <GraphQLSelectUsers
                                onChange={handleOwnerChange}
                                onClearValue={handleClear}
                                selectedId={
                                    edition.campaign.owner ? edition.campaign.owner.id : null
                                }
                                isMulti={false}
                                noResultsText='No users were found'
                            />
                        </FormField>
                    ) : undefined}
                    {!isTemplate && (
                        <FromField
                            loggedInUser={user}
                            owner={edition.campaign.owner}
                            fromEmailAddress={edition.from}
                            onFromEmailAddressChange={(newFromEmailAddress) => {
                                props.onEditionSave({
                                    editionId: edition.id,
                                    fromAddressId: newFromEmailAddress
                                        ? newFromEmailAddress.id
                                        : null,
                                });
                            }}
                        />
                    )}
                    <ToField
                        edition={edition}
                        isTemplate={isTemplate}
                        showPreviewMode={showPreviewMode}
                        shouldShowNewAudienceManagement={shouldShowNewAudienceManagement}
                        setIsPreparingToSend={props.setIsPreparingToSend}
                        onEditionSave={props.onEditionSave}
                        onSetSelectedEditorTab={props.onSetSelectedEditorTab}
                    />
                    <ABTestComparisonFields
                        edition={edition}
                        isABTesting={props.isABTesting}
                        onEditionSave={props.onEditionSave}
                        showPreviewMode={showPreviewMode}
                    />
                    {!showPreviewMode ? (
                        <FormField innerId='useUtmParameters'>
                            <ConditionalCheckboxInput
                                checked={edition.useUtmParameters}
                                label='Add UTM parameters to all links'
                                onChange={(newValue) => {
                                    props.onEditionSave({
                                        editionId: edition.id,
                                        useUtmParameters: newValue,
                                    });
                                }}
                                helpComponent={
                                    <HelpButton
                                        // @ts-expect-error
                                        size={18}
                                        onClick={() => {
                                            setShowUtmHelpModal(true);
                                        }}
                                    />
                                }
                            >
                                <ButtonMover direction={['left', 'up']} size='big'>
                                    <Button
                                        variant='text-primary'
                                        size='big'
                                        onClick={() => {
                                            setShowUtmFormModal(true);
                                        }}
                                    >
                                        Edit parameters
                                    </Button>
                                </ButtonMover>
                            </ConditionalCheckboxInput>
                        </FormField>
                    ) : undefined}
                </div>
                {props.isABTesting ? undefined : (
                    <FormField
                        noMargin={true}
                        tooltipInfo={
                            <div>
                                Use this public link to share your message anywhere: on social
                                media, your website, or just among teammates.
                            </div>
                        }
                        innerId='shareableUrl'
                        label='Shareable URL'
                    >
                        <CopyableLink
                            size='big'
                            name='ShareableUrl'
                            value={editionToDisplay.viewInBrowserLink}
                        />
                    </FormField>
                )}
            </form>
            <UtmHelpModal
                isOpen={showUtmHelpModal}
                onClose={() => {
                    setShowUtmHelpModal(false);
                }}
            />
            <UtmFormModal
                isOpen={showUtmFormModal}
                onClose={() => {
                    setShowUtmFormModal(false);
                }}
                editions={[edition]}
                source={edition.utmParameters.source}
                medium={edition.utmParameters.medium}
                // @ts-expect-error
                onSave={(source, medium, [editionUtmCampaign]: [any]) => {
                    return props.onEditionSave({
                        editionId: edition.id,
                        utmSource: source,
                        utmMedium: medium,
                        utmCampaign: editionUtmCampaign.useDefaultCampaignValue
                            ? null
                            : editionUtmCampaign.utmCampaign,
                    });
                }}
            />
        </>
    );
}
